import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import GenericTable from './GenericTable';
import TablePagination from './TablePagination';
import ColumnToggle from './ColumnToggle';

const GenericView = ({ 
  data,
  columns,
  onEdit,
  onDelete,
  onAdd,
  onView,
  additionalActions,
  checkboxSelection = true,
  customRowActions,
  onRowClick,
  selectedRows: externalSelectedRows,
  onSelectRow: externalOnSelectRow,
  onSelectAllRows: externalOnSelectAllRows,
  visibleColumns: externalVisibleColumns,
  onVisibleColumnsChange: externalOnVisibleColumnsChange,
  isLoading = false,
  emptyStateMessage,
  rowClassName,
  tableClassName,
  initialPageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  showTopControls = true,
  topControlsContent,
  showPagination = true,
  filterComponent,
  searchComponent,
  actionButtons,
  className = '',
  pagination,
  uniqueId = 'default',
  isEditDisabled,
  isDeleteDisabled,
  showColumnToggle = true,
  defaultVisibleColumns,
  onSortChange,
  isRowSelectable,
  defaultSortConfig
}) => {
  // Internal state for selection if not provided by parent
  const [internalSelectedRows, setInternalSelectedRows] = useState([]);
  
  // Internal state for visible columns if not provided by parent
  const [internalVisibleColumns, setInternalVisibleColumns] = useState(
    defaultVisibleColumns || columns.map(col => col.key)
  );
  
  // Use external visibleColumns if provided, otherwise use internal state
  const visibleColumns = externalVisibleColumns !== undefined ? externalVisibleColumns : internalVisibleColumns;
  
  // Use external state if provided, otherwise use internal state
  const selectedRows = externalSelectedRows !== undefined ? externalSelectedRows : internalSelectedRows;
  
  // Jika pagination props diberikan, gunakan server-side pagination
  const isServerSide = !!pagination;

  // State untuk client-side pagination and sorting
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(initialPageSize);
  const [sortConfig, setSortConfig] = useState(defaultSortConfig || { key: null, direction: 'asc' });

  // Untuk server-side, ambil dari props.pagination
  const effectiveCurrentPage = isServerSide ? pagination.currentPage : currentPage;
  const effectiveRecordsPerPage = isServerSide ? pagination.rowsPerPage : recordsPerPage;
  const effectiveTotalRows = isServerSide ? pagination.totalRows : data.length;
  const effectiveOnPageChange = isServerSide ? pagination.onPageChange : setCurrentPage;
  const effectiveOnRowsPerPageChange = isServerSide ? pagination.onRowsPerPageChange : setRecordsPerPage;

  // Separate static rows (total rows) from regular data
  const { staticRows, sortableData } = useMemo(() => {
    const staticRows = [];
    const sortableRows = [];
    
    data.forEach(row => {
      // Check if row should be excluded from sorting and pagination (e.g., total rows)
      // Rows with isStaticRow property or common total row identifiers are excluded
      const isTotalRow = row.isStaticRow || 
                        row.name === 'GRAND TOTAL' || 
                        row.serviceItem === 'GRAND TOTAL' ||
                        row.customerName === 'GRAND TOTAL';
      if (isTotalRow) {
        staticRows.push(row);
      } else {
        sortableRows.push(row);
      }
    });
    
    return { staticRows, sortableData: sortableRows };
  }, [data]);

  // Sort data first (for client-side only)
  const sortedData = useMemo(() => {
    if (isServerSide || !sortConfig.key) return sortableData;
    
    // Sort only the sortable rows
    const sorted = [...sortableData].sort((a, b) => {
      const column = columns.find(col => col.key === sortConfig.key);
      
      // Get values - use sortValue function if provided, otherwise use the key
      let aValue = column?.sortValue ? column.sortValue(a) : a[sortConfig.key];
      let bValue = column?.sortValue ? column.sortValue(b) : b[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle date type
      if (column?.type === 'date') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Convert objects to strings for comparison
      if (typeof aValue === 'object') {
        aValue = JSON.stringify(aValue);
      }
      if (typeof bValue === 'object') {
        bValue = JSON.stringify(bValue);
      }

      // Case-insensitive string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [sortableData, sortConfig, columns, isServerSide]);

  // Calculate pagination for sortable rows only (exclude static rows)
  const sortableTotalRows = isServerSide ? Math.max(0, effectiveTotalRows - staticRows.length) : sortedData.length;
  const totalPages = Math.ceil(sortableTotalRows / effectiveRecordsPerPage);
  
  // Update effectiveTotalRows for client-side to exclude static rows from pagination display
  const displayTotalRows = isServerSide ? effectiveTotalRows : sortableTotalRows;

  // Data yang ditampilkan: server-side = semua data dari parent, client-side = slice after sorting
  // Static rows are always appended at the end, excluded from pagination
  const currentRecords = isServerSide
    ? [...data, ...staticRows]
    : [...sortedData.slice((effectiveCurrentPage - 1) * effectiveRecordsPerPage, effectiveCurrentPage * effectiveRecordsPerPage), ...staticRows];

  // Initialize sort on mount if defaultSortConfig is provided (for server-side)
  useEffect(() => {
    if (defaultSortConfig && defaultSortConfig.key && isServerSide && onSortChange) {
      onSortChange(defaultSortConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Handle sort change
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    const newSortConfig = { key, direction };
    setSortConfig(newSortConfig);
    // Reset to page 1 when sorting changes
    if (!isServerSide) {
      setCurrentPage(1);
    } else {
      // For server-side pagination, notify parent component
      if (onSortChange) {
        onSortChange(newSortConfig);
      }
    }
  };

  // Reset ke halaman 1 jika ganti page size (client-side only)
  const handleRecordsPerPageChange = (size) => {
    if (isServerSide) {
      effectiveOnRowsPerPageChange(size);
    } else {
      setRecordsPerPage(size);
      setCurrentPage(1);
    }
  };

  // Handle row selection
  const handleSelectRow = (itemId, isSelected) => {
    if (externalOnSelectRow) {
      externalOnSelectRow(itemId, isSelected);
    } else {
      setInternalSelectedRows(prev => {
        if (isSelected) {
          return [...prev, itemId];
        } else {
          return prev.filter(id => id !== itemId);
        }
      });
    }
  };

  // Handle select all rows
  const handleSelectAllRows = (itemIds) => {
    if (externalOnSelectAllRows) {
      externalOnSelectAllRows(itemIds);
    } else {
      if (Array.isArray(itemIds) && itemIds.length > 0) {
        // Select all - merge with existing selections from other pages
        setInternalSelectedRows(prev => {
          const newIds = itemIds.filter(id => !prev.includes(id));
          return [...prev, ...newIds];
        });
      } else {
        // Deselect all - remove only current page items
        const currentPageIds = currentRecords.map(item => item.id);
        setInternalSelectedRows(prev => prev.filter(id => !currentPageIds.includes(id)));
      }
    }
  };

  // Handle column visibility toggle
  const handleToggleColumn = (columnKey) => {
    const updateVisibleColumns = (newVisibleColumns) => {
      if (externalOnVisibleColumnsChange) {
        externalOnVisibleColumnsChange(newVisibleColumns);
      } else {
        setInternalVisibleColumns(newVisibleColumns);
      }
    };

    const newVisibleColumns = visibleColumns.includes(columnKey)
      ? (visibleColumns.length > 1 ? visibleColumns.filter(key => key !== columnKey) : visibleColumns)
      : [...visibleColumns, columnKey];
    
    updateVisibleColumns(newVisibleColumns);
  };

  // Handle toggle all columns
  const handleToggleAllColumns = (show) => {
    const updateVisibleColumns = (newVisibleColumns) => {
      if (externalOnVisibleColumnsChange) {
        externalOnVisibleColumnsChange(newVisibleColumns);
      } else {
        setInternalVisibleColumns(newVisibleColumns);
      }
    };

    if (show) {
      updateVisibleColumns(columns.map(col => col.key));
    } else {
      // Keep at least one column visible
      updateVisibleColumns([columns[0].key]);
    }
  };

  return (
    <div className={className}>
      {showTopControls && (
        <div className="row align-items-center w-full mb-3">
          {topControlsContent || (
            <>
              {filterComponent && (
                <div className="col">
                  <div className="col-6 col-sm-4 col-md-2 col-xl">
                    {filterComponent}
                  </div>
                </div>
              )}
              <div className="col-md-auto col-sm-12">
                <div className="ms-auto d-flex flex-wrap btn-list gap-2">
                  {searchComponent}
                  {showColumnToggle && (
                    <ColumnToggle
                      columns={columns}
                      visibleColumns={visibleColumns}
                      onToggleColumn={handleToggleColumn}
                      onToggleAll={handleToggleAllColumns}
                    />
                  )}
                  {actionButtons}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <GenericTable 
        data={currentRecords}
        columns={columns}
        onEdit={onEdit}
        onDelete={onDelete}
        onAdd={onAdd}
        onView={onView}
        additionalActions={additionalActions}
        checkboxSelection={checkboxSelection}
        customRowActions={customRowActions}
        onRowClick={onRowClick}
        selectedRows={selectedRows}
        onSelectRow={handleSelectRow}
        onSelectAllRows={handleSelectAllRows}
        isLoading={isLoading}
        emptyStateMessage={emptyStateMessage}
        rowClassName={rowClassName}
        tableClassName={tableClassName}
        uniqueId={uniqueId}
        sortConfig={sortConfig}
        onSort={handleSort}
        isEditDisabled={isEditDisabled}
        isDeleteDisabled={isDeleteDisabled}
        visibleColumns={visibleColumns}
        isRowSelectable={isRowSelectable}
      />

      {showPagination && displayTotalRows > 0 && (
        <TablePagination
          currentPage={effectiveCurrentPage}
          totalPages={totalPages}
          recordsPerPage={effectiveRecordsPerPage}
          onPageChange={effectiveOnPageChange}
          onRecordsPerPageChange={handleRecordsPerPageChange}
          pageSizeOptions={pageSizeOptions}
          totalRows={displayTotalRows}
        />
      )}
    </div>
  );
};

GenericView.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onAdd: PropTypes.func,
  onView: PropTypes.func,
  additionalActions: PropTypes.func,
  checkboxSelection: PropTypes.bool,
  customRowActions: PropTypes.func,
  onRowClick: PropTypes.func,
  selectedRows: PropTypes.array,
  onSelectRow: PropTypes.func,
  onSelectAllRows: PropTypes.func,
  visibleColumns: PropTypes.arrayOf(PropTypes.string),
  onVisibleColumnsChange: PropTypes.func,
  isLoading: PropTypes.bool,
  emptyStateMessage: PropTypes.string,
  rowClassName: PropTypes.func,
  tableClassName: PropTypes.string,
  initialPageSize: PropTypes.number,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  showTopControls: PropTypes.bool,
  topControlsContent: PropTypes.node,
  showPagination: PropTypes.bool,
  filterComponent: PropTypes.node,
  searchComponent: PropTypes.node,
  actionButtons: PropTypes.node,
  className: PropTypes.string,
  pagination: PropTypes.shape({
    currentPage: PropTypes.number.isRequired,
    totalRows: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
  }),
  uniqueId: PropTypes.string,
  isEditDisabled: PropTypes.func,
  isDeleteDisabled: PropTypes.func,
  showColumnToggle: PropTypes.bool,
  defaultVisibleColumns: PropTypes.arrayOf(PropTypes.string),
  defaultSortConfig: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc'])
  }),
};

export default GenericView;

